/* eslint-disable react/display-name */
import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import SectionComponent from '../components/Section/Section'
import CustomLoader from '../components/Loader/CustomLoader'

// core components
import { deleteSection, editSection, getSections } from '../apollo'
import Header from '../components/Headers/Header'
import DataTable from '../components/DataTable'
import orderBy from 'lodash/orderBy'
import { customStyles } from '../utils/tableCustomStyles'
import useGlobalStyles from '../utils/globalStyles'
import { Box, Container, Modal, Switch, Button } from '@mui/material'
import AddRounded from '@mui/icons-material/AddRounded'
import { raisedButtonSx } from '../components/UI/Depth'
import { RowActionMenu } from '../components/UI/RowActionMenu'
import EditOutlined from '@mui/icons-material/EditOutlined'
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded'
import TableHeader from '../components/TableHeader'
import SearchBar from '../components/TableHeader/SearchBar'
import Alert from '../components/Alert'
import ConfigurableValues from '../config/constants'
import { modalOverlay, modalCard } from '../utils/modalStyles'

const GET_SECTIONS = gql`
  ${getSections}
`
const EDIT_SECTION = gql`
  ${editSection}
`
const DELETE_SECTION = gql`
  ${deleteSection}
`

function Sections(props) {
  const { t } = props
  const { PAID_VERSION } = ConfigurableValues()
  const [editModal, setEditModal] = useState(false)
  const [addModal, setAddModal] = useState(false)
  const [sections, setSections] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const toggleModal = section => {
    setEditModal(!editModal)
    setSections(section)
  }

  // Callback function to close the modal
  const closeEditModal = () => {
    setEditModal(false)
  }

  const restaurantId = localStorage.getItem('restaurantId')

  const [mutateEdit] = useMutation(EDIT_SECTION)
  const [mutateDelete] = useMutation(DELETE_SECTION, {
    refetchQueries: [{ query: GET_SECTIONS }]
  })
  const { data, error: errorQuery, loading: loadingQuery } = useQuery(
    GET_SECTIONS,
    {
      variables: { id: restaurantId }
    }
  )
  console.log(data)

  const customSort = (rows, field, direction) => {
    const handleField = row => {
      if (row[field]) {
        return row[field].toLowerCase()
      }

      return row[field]
    }

    return orderBy(rows, handleField, direction)
  }

  const columns = [
    {
      name: t('Name'),
      sortable: true,
      selector: 'name'
    },
    {
      name: t('Status'),
      sortable: false,
      cell: row => <>{statusChanged(row)}</>
    },
    {
      name: t('Restaurants'),
      sortable: true,
      cell: row => <>{row.restaurants.map(item => `${item.name}`).join(', ')}</>
    },
    {
      name: t('Action'),
      cell: row => <>{actionButtons(row)}</>
    }
  ]

  const statusChanged = row => {
    return (
      <>
        {row.available}
        <Switch
          size="small"
          defaultChecked={row.enabled}
          onChange={() => {
            mutateEdit({
              variables: {
                section: {
                  _id: row._id,
                  name: row.name,
                  enabled: !row.enabled,
                  restaurants: row.restaurants
                    ? row.restaurants.map(r => r._id)
                    : []
                }
              }
            })
          }}
          style={{ color: 'black' }}
        />
      </>
    )
  }
  const actionButtons = row => {
    return (
      <>
        <div>
          <RowActionMenu
            label={t('Action')}
            actions={[
              {
                label: t('Edit'),
                icon: EditOutlined,
                tone: 'primary',
                onClick: () => {
                  if (PAID_VERSION) toggleModal(row)
                  else {
                    setIsOpen(true)
                    setTimeout(() => {
                      setIsOpen(false)
                    }, 5000)
                  }
                }
              },
              {
                label: t('Delete'),
                icon: DeleteOutlineRounded,
                tone: 'danger',
                divider: true,
                confirm: {
                  title: `${t('Delete')}?`,
                  message: t('DeleteConfirm'),
                  cancelLabel: t('Cancel')
                },
                onClick: () => {
                  if (PAID_VERSION) mutateDelete({ variables: { id: row._id } })
                  else {
                    setIsOpen(true)
                    setTimeout(() => {
                      setIsOpen(false)
                    }, 5000)
                  }
                }
              }
            ]}
          />
        </div>
      </>
    )
  }

  const [searchQuery, setSearchQuery] = useState('')
  const onChangeSearch = e => setSearchQuery(e.target.value)
  const regex =
    searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null
  const filtered =
    searchQuery.length < 3
      ? data && data.sections
      : data &&
        data.sections.filter(section => {
          return section.name.toLowerCase().search(regex) > -1
        })

  const globalClasses = useGlobalStyles()

  return (
    <>
      <Header
        action={
          <Button
            startIcon={<AddRounded />}
            onClick={() => setAddModal(true)}
            sx={raisedButtonSx}>
            {`${t('Add')} ${t('Section')}`}
          </Button>
        }
      />
      {/* Page content */}
      <Container className={globalClasses.flex} fluid>
        {isOpen && (
          <Alert message={t('AvailableAfterPurchasing')} severity="warning" />
        )}

        {/* Table */}
        {errorQuery && `${t('Error')}! ${errorQuery.message}`}
        {loadingQuery ? (
          <CustomLoader />
        ) : (
          <DataTable
            title={<TableHeader title={t('RestaurantSections')} />}
            subHeader={true}
            subHeaderComponent={
              <SearchBar
                value={searchQuery}
                onChange={onChangeSearch}
                // onClick={() => refetch()}
              />
            }
            columns={columns}
            data={filtered}
            pagination
            progressPending={loadingQuery}
            progressComponent={<CustomLoader />}
            sortFunction={customSort}
            defaultSortField="name"
            customStyles={customStyles}
            selectableRows
          />
        )}
        <Modal
          open={editModal}
          onClose={() => {
            toggleModal(null)
          }}
          sx={modalOverlay}>
          <Box sx={modalCard('md')}>
            <SectionComponent section={sections} onClose={closeEditModal} />
          </Box>
        </Modal>
        <Modal
          open={addModal}
          onClose={() => setAddModal(false)}
          sx={modalOverlay}>
          <Box sx={modalCard('lg')}>
            <SectionComponent onClose={() => setAddModal(false)} />
          </Box>
        </Modal>
      </Container>
    </>
  )
}

export default withTranslation()(Sections)
